import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FileText,
  Award,
  MessageSquare,
  BarChart3,
  CheckCircle,
  Users,
  Search,
  Sparkles,
  Target,
  Heart,
  TrendingUp,
  UserX,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Send,
  ThumbsUp,
  Save,
} from 'lucide-react';
import NarrativeCompilationPanel from './components/NarrativeCompilationPanel';
import SurfedStrengthsSkillsPanel from './components/SurfedStrengthsSkillsPanel';
import NarrativePromptsPanel from './components/NarrativePromptsPanel';
import ComparativeContextPanel from './components/ComparativeContextPanel';
import DecisionOutputsPanel from './components/DecisionOutputsPanel';
import {
  generateSnapshot,
  updateSnapshot,
  submitSnapshot,
  acknowledgeSnapshot,
  PerformanceSnapshotView,
  SnapshotUpdateInput,
} from '../../api/PreformanceManagement/PerformanceSnapshot';
import request from '../../utils/request';

export interface Employee {
  id: string;
  name: string;
  position: string;
  department: string;
  manager: string;
  lastReviewDate: string;
  nextReviewDue: string;
  imageUrl?: string | null;
}

export interface ReviewData {
  employeeId: string;
  generatedAt: string;
  snapshotId: number | null;
  snapshot: PerformanceSnapshotView | null;
  narrative: Record<string, unknown> | null;
  strengths: Record<string, unknown> | null;
  prompts: Record<string, string> | null;
  comparative: Record<string, unknown> | null;
  decisions: Record<string, unknown> | null;
}

const PerformanceSnapshot = () => {
  const { t } = useTranslation();
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState('narrative');
  const [reviewData, setReviewData] = useState<ReviewData | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load employees from the Recognition/Recipients API
  useEffect(() => {
    const fetchEmployees = async () => {
      setIsLoadingEmployees(true);
      try {
        const response = await request.get('Recognition/Recipients');
        const data = response.data;
        const payload = data?.payload ?? data;
        if (Array.isArray(payload) && payload.length > 0) {
          const mapped: Employee[] = payload.map(
            (r: {
              recipientId: string;
              recipientFirstName: string;
              recipientLastName: string;
              recipientProfilePictureUrl?: string;
            }) => ({
              id: r.recipientId,
              name: `${r.recipientFirstName} ${r.recipientLastName}`.trim(),
              position: '',
              department: '',
              manager: '',
              lastReviewDate: '',
              nextReviewDue: '',
              imageUrl: r.recipientProfilePictureUrl || null,
            }),
          );
          setEmployees(mapped);
        }
      } catch {
        setError(t('performanceSnapshot.loadError'));
      } finally {
        setIsLoadingEmployees(false);
      }
    };
    fetchEmployees();
  }, [t]);

  const filteredEmployees = employees.filter(
    (emp) =>
      emp.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      emp.department.toLowerCase().includes(searchQuery.toLowerCase()) ||
      emp.position.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const handleGenerateReview = async () => {
    if (!selectedEmployee) return;

    setIsGenerating(true);
    setError(null);
    try {
      const snapshot = await generateSnapshot(selectedEmployee.id);
      const safeParse = (json: string | null | undefined) => {
        if (!json) return null;
        try { return JSON.parse(json); } catch { return null; }
      };
      setReviewData({
        employeeId: selectedEmployee.id,
        generatedAt: new Date().toISOString(),
        snapshotId: snapshot.id,
        snapshot,
        narrative: null,
        strengths: safeParse(snapshot.strengthsSummary),
        prompts: safeParse(snapshot.narrativePromptsSummary),
        comparative: null,
        decisions: safeParse(snapshot.decisionsSummary),
      });
      setActiveTab('narrative');
    } catch {
      setError(t('performanceSnapshot.generateError'));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSaveSnapshot = async () => {
    if (!reviewData?.snapshotId) return;
    try {
      const input: SnapshotUpdateInput = {};
      if (reviewData.decisions) input.decisionsSummary = JSON.stringify(reviewData.decisions);
      if (reviewData.prompts) input.narrativePromptsSummary = JSON.stringify(reviewData.prompts);
      if (reviewData.strengths) input.strengthsValidation = JSON.stringify(reviewData.strengths);
      const updated = await updateSnapshot(reviewData.snapshotId, input);
      setReviewData(prev => prev ? { ...prev, snapshot: updated } : prev);
    } catch {
      setError(t('performanceSnapshot.saveError'));
    }
  };

  const handleSubmitSnapshot = async () => {
    if (!reviewData?.snapshotId) return;
    try {
      await handleSaveSnapshot(); // save first
      const updated = await submitSnapshot(reviewData.snapshotId);
      setReviewData(prev => prev ? { ...prev, snapshot: updated } : prev);
    } catch {
      setError(t('performanceSnapshot.submitError'));
    }
  };

  const handleAcknowledgeSnapshot = async () => {
    if (!reviewData?.snapshotId) return;
    try {
      const updated = await acknowledgeSnapshot(reviewData.snapshotId);
      setReviewData(prev => prev ? { ...prev, snapshot: updated } : prev);
    } catch {
      setError(t('performanceSnapshot.acknowledgeError'));
    }
  };

  const tabs = [
    { id: 'narrative', label: t('performanceSnapshot.tabs.narrative'), icon: FileText },
    { id: 'strengths-skills', label: t('performanceSnapshot.tabs.strengthsSkills'), icon: Award },
    { id: 'prompts', label: t('performanceSnapshot.tabs.prompts'), icon: MessageSquare },
    { id: 'comparative', label: t('performanceSnapshot.tabs.comparative'), icon: BarChart3 },
    { id: 'decisions', label: t('performanceSnapshot.tabs.decisions'), icon: CheckCircle },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-500/10 via-indigo-500/10 to-purple-500/10 border-b border-border">
        <div className="max-w-[1600px] mx-auto px-6 py-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center space-x-3 mb-2">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
                  <FileText size={24} color="white" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-foreground">
                    {t('performanceSnapshot.title')}
                  </h1>
                  <p className="text-muted-foreground mt-1">
                    {t('performanceSnapshot.subtitle')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1600px] mx-auto px-6 py-8">
        {/* Error Banner */}
        {error && (
          <div className="mb-6 flex items-center space-x-3 p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive">
            <AlertCircle size={20} />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {/* Employee Selection */}
        <div className="mb-6">
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
                <Users size={20} />
                <span>{t('performanceSnapshot.selectEmployee')}</span>
              </h3>
              <button
                onClick={handleGenerateReview}
                disabled={!selectedEmployee || isGenerating}
                className="flex items-center space-x-2 px-5 py-2.5 rounded-lg text-sm font-medium text-white bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                {isGenerating ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    <span>{t('performanceSnapshot.generating')}</span>
                  </>
                ) : (
                  <>
                    <Sparkles size={18} />
                    <span>{t('performanceSnapshot.generate')}</span>
                  </>
                )}
              </button>
            </div>

            {/* Search */}
            <div className="mb-4 relative">
              <Search
                size={18}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
              />
              <input
                type="text"
                placeholder={t('performanceSnapshot.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary text-sm"
              />
            </div>

            {/* Employee Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {isLoadingEmployees ? (
                <div className="col-span-full text-center py-8 text-muted-foreground">
                  <Loader2 size={32} className="mx-auto mb-2 animate-spin opacity-50" />
                  <p className="text-sm">{t('performanceSnapshot.loadingEmployees')}</p>
                </div>
              ) : filteredEmployees.length === 0 ? (
                <div className="col-span-full text-center py-8 text-muted-foreground">
                  <UserX size={32} className="mx-auto mb-2 opacity-50" />
                  <p className="text-sm">{t('performanceSnapshot.noEmployeesFound')}</p>
                </div>
              ) : (
                filteredEmployees.map((employee) => (
                  <button
                    key={employee.id}
                    onClick={() => setSelectedEmployee(employee)}
                    className={`flex flex-col items-center space-y-2 p-4 rounded-lg border transition-all duration-200 ${
                      selectedEmployee?.id === employee.id
                        ? 'border-primary bg-primary/5 shadow-sm'
                        : 'border-border hover:border-primary/50 hover:bg-muted/30'
                    }`}
                  >
                    {employee.imageUrl ? (
                      <img
                        src={employee.imageUrl}
                        alt={employee.name}
                        className="w-16 h-16 rounded-full object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white font-semibold flex-shrink-0">
                        {employee.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="text-center">
                      <div className="font-semibold text-foreground text-sm">{employee.name}</div>
                      {employee.position && (
                        <div className="text-xs text-muted-foreground">{employee.position}</div>
                      )}
                      {employee.department && (
                        <div className="text-xs text-muted-foreground">{employee.department}</div>
                      )}
                    </div>
                    {selectedEmployee?.id === employee.id && (
                      <CheckCircle2 size={18} className="text-primary" />
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Snapshot Summary Card */}
        {reviewData?.snapshot && (
          <div className="mb-6 bg-card border border-border rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">
                {t('performanceSnapshot.snapshotSummary')}
              </h3>
              <span className="text-sm text-muted-foreground">
                {t('performanceSnapshot.period')}: {reviewData.snapshot.period}
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-muted/30 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-foreground">
                  {reviewData.snapshot.overallScore != null
                    ? `${Math.round(reviewData.snapshot.overallScore)}%`
                    : '--'}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {t('performanceSnapshot.overallScore')}
                </div>
              </div>
              {reviewData.snapshot.goalsSummary && (() => {
                try {
                  const goals = JSON.parse(reviewData.snapshot.goalsSummary);
                  return (
                    <div className="bg-muted/30 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-foreground">
                        {goals.completed}/{goals.total}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {t('performanceSnapshot.goalsCompleted')}
                      </div>
                    </div>
                  );
                } catch { return null; }
              })()}
              {reviewData.snapshot.checkInsSummary && (() => {
                try {
                  const checkIns = JSON.parse(reviewData.snapshot.checkInsSummary);
                  return (
                    <div className="bg-muted/30 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-foreground">
                        {checkIns.total}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {t('performanceSnapshot.checkInsCompleted')}
                      </div>
                    </div>
                  );
                } catch { return null; }
              })()}
              {reviewData.snapshot.recognitionSummary && (() => {
                try {
                  const recognition = JSON.parse(reviewData.snapshot.recognitionSummary);
                  return (
                    <div className="bg-muted/30 rounded-lg p-4 text-center">
                      <div className="text-2xl font-bold text-foreground">
                        {recognition.received}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {t('performanceSnapshot.recognitionsReceived')}
                      </div>
                    </div>
                  );
                } catch { return null; }
              })()}
            </div>
          </div>
        )}

        {/* Status Bar */}
        {reviewData?.snapshot && (
          <div className="mb-6 flex items-center justify-between bg-card border border-border rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <span className="text-sm font-medium text-muted-foreground">{t('performanceSnapshot.status')}:</span>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                reviewData.snapshot.status === 1 ? 'bg-yellow-100 text-yellow-800' :
                reviewData.snapshot.status === 2 ? 'bg-blue-100 text-blue-800' :
                'bg-green-100 text-green-800'
              }`}>
                {reviewData.snapshot.status === 1 ? t('performanceSnapshot.statusDraft') :
                 reviewData.snapshot.status === 2 ? t('performanceSnapshot.statusSubmitted') :
                 t('performanceSnapshot.statusAcknowledged')}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              {reviewData.snapshot.status === 1 && (
                <>
                  <button onClick={handleSaveSnapshot} className="flex items-center space-x-2 px-4 py-2 border border-border rounded-lg text-sm font-medium text-foreground hover:bg-muted/50 transition-colors">
                    <Save size={16} />
                    <span>{t('performanceSnapshot.saveDraft')}</span>
                  </button>
                  <button onClick={handleSubmitSnapshot} className="flex items-center space-x-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors">
                    <Send size={16} />
                    <span>{t('performanceSnapshot.submit')}</span>
                  </button>
                </>
              )}
              {reviewData.snapshot.status === 2 && (
                <button onClick={handleAcknowledgeSnapshot} className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors">
                  <ThumbsUp size={16} />
                  <span>{t('performanceSnapshot.acknowledge')}</span>
                </button>
              )}
            </div>
          </div>
        )}

        {/* Content Area */}
        {!reviewData ? (
          <div className="bg-card border border-border rounded-lg p-12 text-center">
            <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText size={40} className="text-purple-600" />
            </div>
            <h3 className="text-xl font-semibold text-foreground mb-2">
              {t('performanceSnapshot.emptyState.title')}
            </h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              {t('performanceSnapshot.emptyState.description')}
            </p>
            <div className="grid grid-cols-2 gap-4 max-w-2xl mx-auto mt-8">
              <div className="bg-muted/30 rounded-lg p-4">
                <Target size={24} className="text-purple-600 mx-auto mb-2" />
                <p className="text-sm font-medium text-foreground">
                  {t('performanceSnapshot.emptyState.goalsAnalysis')}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('performanceSnapshot.emptyState.goalsAnalysisDesc')}
                </p>
              </div>
              <div className="bg-muted/30 rounded-lg p-4">
                <Heart size={24} className="text-purple-600 mx-auto mb-2" />
                <p className="text-sm font-medium text-foreground">
                  {t('performanceSnapshot.emptyState.impactHighlights')}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('performanceSnapshot.emptyState.impactHighlightsDesc')}
                </p>
              </div>
              <div className="bg-muted/30 rounded-lg p-4">
                <MessageSquare size={24} className="text-purple-600 mx-auto mb-2" />
                <p className="text-sm font-medium text-foreground">
                  {t('performanceSnapshot.emptyState.feedbackThemes')}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('performanceSnapshot.emptyState.feedbackThemesDesc')}
                </p>
              </div>
              <div className="bg-muted/30 rounded-lg p-4">
                <TrendingUp size={24} className="text-purple-600 mx-auto mb-2" />
                <p className="text-sm font-medium text-foreground">
                  {t('performanceSnapshot.emptyState.behaviorPatterns')}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {t('performanceSnapshot.emptyState.behaviorPatternsDesc')}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Tab Navigation */}
            <div className="bg-card border border-border rounded-lg p-2">
              <div className="flex space-x-2">
                {tabs.map((tab) => {
                  const TabIcon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex-1 flex items-center justify-center space-x-2 px-6 py-4 rounded-md text-sm font-medium transition-colors duration-200 ${
                        activeTab === tab.id
                          ? 'bg-primary text-primary-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                      }`}
                    >
                      <TabIcon size={16} />
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Tab Content */}
            <div
              key={activeTab}
              className="transition-all duration-300 ease-in-out"
              style={{ animation: 'fadeIn 0.3s ease-in-out' }}
            >
              {activeTab === 'narrative' && selectedEmployee && (
                <NarrativeCompilationPanel
                  employee={selectedEmployee}
                  reviewData={reviewData}
                  onNarrativeUpdate={(narrative) =>
                    setReviewData({ ...reviewData, narrative })
                  }
                />
              )}
              {activeTab === 'strengths-skills' && selectedEmployee && (
                <SurfedStrengthsSkillsPanel
                  employee={selectedEmployee}
                  reviewData={reviewData}
                  onStrengthsValidationUpdate={(validation) =>
                    setReviewData({ ...reviewData, strengths: validation })
                  }
                />
              )}
              {activeTab === 'prompts' && selectedEmployee && (
                <NarrativePromptsPanel
                  employee={selectedEmployee}
                  reviewData={reviewData}
                  onPromptsUpdate={(prompts) =>
                    setReviewData({ ...reviewData, prompts })
                  }
                />
              )}
              {activeTab === 'comparative' && selectedEmployee && (
                <ComparativeContextPanel
                  employee={selectedEmployee}
                  reviewData={reviewData}
                />
              )}
              {activeTab === 'decisions' && selectedEmployee && (
                <DecisionOutputsPanel
                  employee={selectedEmployee}
                  reviewData={reviewData}
                  onDecisionsUpdate={(decisions) =>
                    setReviewData({ ...reviewData, decisions })
                  }
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PerformanceSnapshot;
